<script setup lang="ts">
import Tags from './Tags.vue'
import Footer from './Footer.vue'
import Header from './Header.vue'
import type { INews } from '~/components/entity/news'
import { WidgetNewsImagesSlider } from '~/components/widget/news-images-slider'
import { TtsControls } from '~/components/ui/tts-controls'
import { FavoriteButton } from '~/components/ui/favorite-button'
import { LikeButton } from '~/components/ui/like-button'
import { NewsContent } from '~/components/ui/news-content'

const props = defineProps<INews>()

// TTS text preparation
const { getTextContent } = useTTS()
const ttsText = computed(() => {
  return `${props.title}. ${props.summary || ''}. ${getTextContent(props.content)}`
})

// Kyrgyz needs a slug — the TTS service keys its audio by it. Russian falls back
// to browser speech synthesis, which needs neither.
const showTts = computed(() => props.language !== 'ky' || Boolean(props.slug))
</script>

<template>
  <div class="overflow-hidden rounded-lg bg-white">
    <div class="space-y-5 px-4 py-6 sm:px-6">
      <div class="flex items-start justify-between gap-3 sm:gap-4">
        <Heading tag="h1" class="h2 flex-1">{{ props.title }}</Heading>
        <div class="flex shrink-0 gap-2">
          <LikeButton :id="props.id" :like-count="props.likes_count || 0" variant="outline" />
          <FavoriteButton :id="props.id" variant="outline" />
        </div>
      </div>
      <p class="text-lg">{{ props.summary }}</p>
      <hr v-if="!props.largeImage" />
      <Header
        v-else
        :large-image="props.largeImage"
        :title="props.title"
        :large-image-copyright="props.largeImageCopyright"
      />

      <NewsContent :data="props.content" />

      <WidgetNewsImagesSlider
        v-if="props.carousel && props.carousel.length > 0"
        :images="props.carousel"
      />

      <iframe
        v-if="props.youtube"
        :src="`https://www.youtube.com/embed/${props.youtube}`"
        frameborder="0"
        allow="
          accelerometer;
          autoplay;
          clipboard-write;
          encrypted-media;
          gyroscope;
          picture-in-picture;
          web-share;
        "
        referrerpolicy="strict-origin-when-cross-origin"
        allowfullscreen
        loading="lazy"
        sandbox="allow-same-origin allow-scripts allow-popups allow-presentation"
        class="h-[400px] w-full overflow-hidden rounded-lg sm:h-[600px]"
      />

      <Tags v-if="props.tags" :tags="props.tags" />

      <!-- TTS Controls -->
      <TtsControls v-if="showTts" :text="ttsText" :slug="props.slug" :language="props.language" />
    </div>
    <Footer
      :views_count="props.views_count"
      :publication_datetime="props.publication_datetime"
      :categories="props.categories"
      :user="props.user"
    />
  </div>
</template>
